import { Button, Checkbox } from "@dash/form";
import { Header4, Page, PageHeader } from "@dash/page";
import { Skeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn, TableRowProps } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons/lib";
import { MdDownload, MdRefresh, MdUpload } from "react-icons/md";

export type DiffSyncRecord<T extends Record<string, unknown>> = {
  diffId: string;
  uniqueKeys: string[];
  local: T | null;
  remote: T | null;
  error: string | null;
  isPushable: boolean;
  isPullable: boolean;
};

type DiffSyncDirection = "pull" | "push";
type ValueFormatter = (value: unknown) => React.ReactNode;

type Props<T extends Record<string, unknown>> = {
  title: string;
  pullableDiffs: DiffSyncRecord<T>[] | undefined;
  pushableDiffs: DiffSyncRecord<T>[] | undefined;
  errorDiffs?: DiffSyncRecord<T>[];
  directionLabels?: Partial<Record<DiffSyncDirection, string>>;
  labels?: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, ValueFormatter>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  isExecuting?: boolean;
  onRefresh: () => Promise<unknown>;
  onPull: (diffIds: string[]) => Promise<unknown>;
  onPush: (diffIds: string[]) => Promise<unknown>;
};

export const DiffSyncPage = <T extends Record<string, unknown>>(props: Props<T>) => {
  const [activeTab, setActiveTab] = useState<DiffSyncDirection>("pull");
  const [selectedDiffIds, setSelectedDiffIds] = useState<Record<DiffSyncDirection, string[]>>({
    pull: [],
    push: [],
  });
  const activeDiffs = activeTab === "pull" ? props.pullableDiffs : props.pushableDiffs;
  const activeDiffIds = useMemo(() => (activeDiffs ?? []).map((diff) => diff.diffId), [activeDiffs]);
  const activeDiffIdSet = useMemo(() => new Set(activeDiffIds), [activeDiffIds]);
  const activeSelectedDiffIds = selectedDiffIds[activeTab].filter((id) => activeDiffIdSet.has(id));
  const directionLabels = {
    pull: props.directionLabels?.pull ?? "Pull",
    push: props.directionLabels?.push ?? "Push",
  };
  const isBusy = props.isLoading || props.isRefreshing || props.isExecuting;

  useEffect(() => {
    setSelectedDiffIds((current) => ({
      ...current,
      pull: props.pullableDiffs?.map((diff) => diff.diffId) ?? [],
    }));
  }, [props.pullableDiffs]);

  useEffect(() => {
    setSelectedDiffIds((current) => ({
      ...current,
      push: props.pushableDiffs?.map((diff) => diff.diffId) ?? [],
    }));
  }, [props.pushableDiffs]);

  const execute = async () => {
    if (activeTab === "pull") await props.onPull(activeSelectedDiffIds);
    if (activeTab === "push") await props.onPush(activeSelectedDiffIds);
  };

  const setSelection = (direction: DiffSyncDirection) => (diffIds: string[]) => {
    setSelectedDiffIds((current) => ({ ...current, [direction]: diffIds }));
  };

  return (
    <Page css={{ maxWidth: "100%", gap: "1rem", display: "flex", flexDirection: "column" }}>
      <div css={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <PageHeader>{props.title}</PageHeader>
        <div css={{ flexGrow: 1 }} />
        <Button
          variant="outline"
          icon={<MdRefresh />}
          onClick={() => void props.onRefresh()}
          isLoading={props.isRefreshing}
          isDisabled={isBusy}
        >
          Odśwież
        </Button>
        <Button
          icon={activeTab === "pull" ? <MdDownload /> : <MdUpload />}
          onClick={() => void execute()}
          isLoading={props.isExecuting}
          isDisabled={isBusy || activeSelectedDiffIds.length === 0}
        >
          {directionLabels[activeTab]}
        </Button>
      </div>

      <DiffSyncErrorsTable
        diffs={props.errorDiffs}
        labels={props.labels}
        formatters={props.formatters}
        alwaysVisibleColumns={props.alwaysVisibleColumns}
        isLoading={props.isLoading}
      />

      <DiffTabs
        activeTab={activeTab}
        directionLabels={directionLabels}
        pullCount={props.pullableDiffs?.length}
        pushCount={props.pushableDiffs?.length}
        onChange={setActiveTab}
      />

      <DiffSyncTable
        direction={activeTab}
        diffs={activeDiffs}
        selectedDiffIds={selectedDiffIds[activeTab]}
        onSelectionChange={setSelection(activeTab)}
        labels={props.labels}
        formatters={props.formatters}
        alwaysVisibleColumns={props.alwaysVisibleColumns}
        isLoading={props.isLoading}
      />
    </Page>
  );
};

const DiffTabs = (props: {
  activeTab: DiffSyncDirection;
  directionLabels: Record<DiffSyncDirection, string>;
  pullCount: number | undefined;
  pushCount: number | undefined;
  onChange: (tab: DiffSyncDirection) => void;
}) => {
  const theme = useTheme();

  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "0.5rem",
        width: "100%",
        padding: "0.25rem",
        border: `1px solid ${theme.colors.primaryLight}`,
        borderRadius: theme.smallBorderRadius,
        background: "white",
      }}
    >
      <DiffTab
        isActive={props.activeTab === "pull"}
        label={props.directionLabels.pull}
        count={props.pullCount}
        Icon={MdDownload}
        onClick={() => props.onChange("pull")}
      />
      <DiffTab
        isActive={props.activeTab === "push"}
        label={props.directionLabels.push}
        count={props.pushCount}
        Icon={MdUpload}
        onClick={() => props.onChange("push")}
      />
    </div>
  );
};

const DiffTab = (props: {
  isActive: boolean;
  label: string;
  count: number | undefined;
  Icon: IconType;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const Icon = props.Icon;

  return (
    <button
      onClick={props.onClick}
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.75rem",
        width: "100%",
        minWidth: 0,
        padding: "0.75rem 1rem",
        border: "none",
        borderRadius: theme.smallBorderRadius,
        background: props.isActive ? theme.colors.background : "transparent",
        color: theme.colors.primary,
        cursor: "pointer",
        fontSize: "0.9375rem",
        transition: "background 0.1s ease-in-out",
      }}
    >
      <span css={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: props.isActive ? 700 : 400 }}>
        <Icon size={18} />
        {props.label}
      </span>
      <span
        css={{
          minWidth: "2rem",
          padding: "0.125rem 0.5rem",
          borderRadius: "999px",
          background: props.isActive ? theme.colors.primary : theme.colors.primaryLight,
          color: props.isActive ? "white" : theme.colors.primary,
          fontWeight: 700,
          fontSize: "0.8125rem",
          textAlign: "center",
        }}
      >
        {props.count ?? "-"}
      </span>
    </button>
  );
};

const DiffSyncErrorsTable = <T extends Record<string, unknown>>(props: {
  diffs: DiffSyncRecord<T>[] | undefined;
  labels?: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, ValueFormatter>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
  isLoading?: boolean;
}) => {
  const theme = useTheme();

  if (!props.diffs) return props.isLoading ? <Skeleton /> : null;
  if (props.diffs.length === 0) return null;

  const contextColumns = props.alwaysVisibleColumns ?? [];
  const columns: TableHeadColumn<string>[] = [
    { label: "Id" },
    { label: "Błąd" },
    ...contextColumns.map((key) => ({ label: props.labels?.[key] ?? key })),
  ];

  const errorCss: CSSObject = {
    background: theme.colors.dangerBackground,
    color: theme.colors.danger,
    fontWeight: 700,
  };

  const rows: TableRowProps[] = props.diffs.map((diff) => {
    const source = diff.local ?? diff.remote;
    const baseline = diff.remote ?? diff.local;

    return {
      id: diff.diffId,
      cellsContent: [
        {
          element: diff.diffId,
          cellCss: errorCss,
        },
        {
          element: diff.error ?? "-",
          cellCss: errorCss,
        },
        ...contextColumns.map<RowCellContent>((key) => ({
          element: formatValue(getCellValue(source, baseline, key, false), props.formatters?.[key]),
        })),
      ],
    };
  });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <Header4>Błędy</Header4>
      <div css={{ overflow: "auto" }}>
        <Table
          columns={columns}
          css={{
            whiteSpace: "nowrap",
          }}
        >
          <TableBody isLoading={props.isLoading} rows={rows} />
        </Table>
      </div>
    </div>
  );
};

const DiffSyncTable = <T extends Record<string, unknown>>(props: {
  direction: DiffSyncDirection;
  diffs: DiffSyncRecord<T>[] | undefined;
  selectedDiffIds: readonly string[];
  onSelectionChange: (diffIds: string[]) => void;
  labels?: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, ValueFormatter>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
  isLoading?: boolean;
}) => {
  const theme = useTheme();
  const columns = useMemo(
    () => getVisibleColumns(props.diffs ?? [], props.direction, props.alwaysVisibleColumns ?? []),
    [props.alwaysVisibleColumns, props.diffs, props.direction],
  );
  const selectedDiffIdSet = useMemo(() => new Set(props.selectedDiffIds), [props.selectedDiffIds]);

  if (!props.diffs) return <Skeleton />;

  const diffIds = props.diffs.map((diff) => diff.diffId);
  const visibleSelectedDiffIds = props.selectedDiffIds.filter((id) => diffIds.includes(id));
  const isAllSelected = diffIds.length > 0 && visibleSelectedDiffIds.length === diffIds.length;
  const selectionCellCss: CSSObject = {
    width: "1px",
    textAlign: "center",
  };

  const allColumns: TableHeadColumn<string>[] = [
    {
      label: "",
      element: (
        <Checkbox
          value={isAllSelected}
          onChange={(isSelected) => props.onSelectionChange(isSelected ? diffIds : [])}
          isDisabled={diffIds.length === 0}
        />
      ),
      cellCss: selectionCellCss,
    },
    { label: "Id" },
    ...columns.map((key) => ({ label: props.labels?.[key] ?? key })),
  ];

  const newRecordCss: CSSObject = {
    background: theme.colors.primaryLight,
    color: theme.colors.primary,
    fontWeight: 700,
  };

  const modificationCss: CSSObject = {
    background: theme.colors.warningBackground,
    color: theme.colors.warning,
    fontWeight: 700,
  };

  const deletionCss: CSSObject = {
    background: theme.colors.dangerBackground,
    color: theme.colors.danger,
    fontWeight: 700,
  };

  const rows: TableRowProps[] = props.diffs.map((diff) => {
    const changedFields = getChangedFields(diff, props.direction);
    const isNew = isNewRecord(diff, props.direction);
    const source = getDirectionValue(diff, props.direction);
    const baseline = getBaselineValue(diff, props.direction);
    const isSelected = selectedDiffIdSet.has(diff.diffId);
    const toggleSelection = (selected: boolean) => {
      if (selected) {
        props.onSelectionChange(Array.from(new Set([...visibleSelectedDiffIds, diff.diffId])));
      } else {
        props.onSelectionChange(visibleSelectedDiffIds.filter((id) => id !== diff.diffId));
      }
    };

    return {
      id: diff.diffId,
      cellsContent: [
        {
          element: <Checkbox value={isSelected} onChange={toggleSelection} />,
          cellCss: selectionCellCss,
        },
        {
          element: diff.diffId,
          cellCss: isNew ? newRecordCss : undefined,
        },
        ...columns.map<RowCellContent>((key) => {
          const isChanged = changedFields.has(key);
          const isAddition = isChanged && !isNew && isFieldAddition(source, baseline, key);
          const isDeletion = isChanged && !isNew && isFieldDeletion(source, baseline, key);

          return {
            element: renderCellValue(source, baseline, key, isChanged, isNew, props.formatters?.[key]),
            cellCss: isNew || isAddition ? newRecordCss : isDeletion ? deletionCss : isChanged ? modificationCss : undefined,
          };
        }),
      ],
    };
  });

  return (
    <div css={{ overflow: "auto" }}>
      <Table
        columns={allColumns}
        css={{
          whiteSpace: "nowrap",
        }}
      >
        <TableBody isLoading={props.isLoading} rows={rows} />
      </Table>
      {rows.length === 0 && <div css={{ padding: "1rem", color: theme.colors.primary, opacity: 0.75 }}>Bez zmian</div>}
    </div>
  );
};

const getVisibleColumns = <T extends Record<string, unknown>>(
  diffs: DiffSyncRecord<T>[],
  direction: DiffSyncDirection,
  alwaysVisibleColumns: readonly (keyof T & string)[],
) =>
  Array.from(
    new Set([...alwaysVisibleColumns, ...diffs.flatMap((diff) => Array.from(getChangedFields(diff, direction)))]),
  );

const getChangedFields = <T extends Record<string, unknown>>(diff: DiffSyncRecord<T>, direction: DiffSyncDirection) => {
  const source = getDirectionValue(diff, direction);
  const baseline = getBaselineValue(diff, direction);

  if (!source) return new Set<keyof T & string>();
  if (!baseline) return new Set(Object.keys(source) as (keyof T & string)[]);

  const keys = Array.from(new Set([...Object.keys(source), ...Object.keys(baseline)]));

  return new Set(keys.filter((key) => !_.isEqual(source[key], baseline[key])) as (keyof T & string)[]);
};

const isNewRecord = <T extends Record<string, unknown>>(diff: DiffSyncRecord<T>, direction: DiffSyncDirection) =>
  !!getDirectionValue(diff, direction) && !getBaselineValue(diff, direction);

const isFieldAddition = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
) => {
  const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
  const newValue = source && hasOwn(source, key) ? source[key] : undefined;

  return isEmptyValue(oldValue) && !isEmptyValue(newValue);
};

const isFieldDeletion = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
) => {
  const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
  const newValue = source && hasOwn(source, key) ? source[key] : undefined;

  return !isEmptyValue(oldValue) && isEmptyValue(newValue);
};

const getDirectionValue = <T extends Record<string, unknown>>(diff: DiffSyncRecord<T>, direction: DiffSyncDirection) =>
  direction === "pull" ? diff.remote : diff.local;

const getBaselineValue = <T extends Record<string, unknown>>(diff: DiffSyncRecord<T>, direction: DiffSyncDirection) =>
  direction === "pull" ? diff.local : diff.remote;

const getCellValue = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
  isChanged: boolean,
) => {
  if (source && hasOwn(source, key)) return source[key];
  if (isChanged) return undefined;
  if (baseline && hasOwn(baseline, key)) return baseline[key];

  return undefined;
};

const renderCellValue = <T extends Record<string, unknown>>(
  source: T | null,
  baseline: T | null,
  key: keyof T & string,
  isChanged: boolean,
  isNew: boolean,
  formatter?: ValueFormatter,
) => {
  if (isChanged && !isNew) {
    const oldValue = baseline && hasOwn(baseline, key) ? baseline[key] : undefined;
    const newValue = source && hasOwn(source, key) ? source[key] : undefined;

    if (isEmptyValue(oldValue)) return formatValue(newValue, formatter);
    if (isEmptyValue(newValue)) return <RemovedValue value={oldValue} formatter={formatter} />;

    return <ValueChange oldValue={oldValue} newValue={newValue} formatter={formatter} />;
  }

  return formatValue(getCellValue(source, baseline, key, isChanged), formatter);
};

const ValueChange = (props: { oldValue: unknown; newValue: unknown; formatter?: ValueFormatter }) => (
  <span css={{ display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
    <span css={{ fontWeight: 400, opacity: 0.75 }}>{formatValue(props.oldValue, props.formatter)}</span>
    <span>→</span>
    <span>{formatValue(props.newValue, props.formatter)}</span>
  </span>
);

const RemovedValue = (props: { value: unknown; formatter?: ValueFormatter }) => {
  const theme = useTheme();

  return (
    <span css={{ color: theme.colors.danger, fontWeight: 400, textDecoration: "line-through" }}>
      {formatValue(props.value, props.formatter)}
    </span>
  );
};

const hasOwn = <T extends Record<string, unknown>>(value: T, key: keyof T & string) =>
  Object.prototype.hasOwnProperty.call(value, key);

const isEmptyValue = (value: unknown) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

const formatValue = (value: unknown, formatter?: ValueFormatter): React.ReactNode => {
  if (isEmptyValue(value)) return "-";
  if (formatter) return formatter(value);
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value.toString();
  if (Array.isArray(value)) return value.map((item) => formatValue(item)).join(", ");

  return JSON.stringify(value);
};
